/**
 * @lines98/client/components/gnome-card — КАРТОЧКА гнома (ветка trophy-card; горизонтальная
 * витрина-плашка, идентична jewelry-card по форме, но без кольца камней).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (code-structure §1/§2 «ствол → ветки», owner-directive 2026-06-14
 * «точная копия Мастерской — просто сделай точную копию, вообще не думая»).
 * Карточка гнома — это ветка `trophy-card` (тот же каркас 3 секций: [герой][инфо][действие]),
 * как `jewelry-card` — но БЕЗ кольца камней. Секция герой = спрайт гнома (во всю высоту
 * плашки, правка #1); секция инфо = имя (тоньше, правка #3) + уровень-бейдж + 3 бонуса
 * столбцом (gold/shards/gem, правка #2); секция действие = цена над кнопкой «Улучшить» (правка #4).
 * Экран «Гномы» выглядит и ведёт себя как «Мастерская» (owner-baseline).
 *
 * owner-directive 2026-06-14 «гном-специфика поверх базы»:
 *   #1 Спрайт КРУПНЫЙ, от вьюпорта (feat/gnome-card-bigger): width=height=--gnc-hero-size
 *      = max(80px, cell·2.5) → гном диктует высоту карточки (перевёрнутая зависимость).
 *      padding:--m (ЕДИНЫЙ зазор: сверху/снизу/слева = --m; справа до контента = --m
 *      через gap родителя .trophy-card-row). Спрайт = (hero-size − 2·--m)² — квадрат.
 *   #2 3 бонуса столбцом — gold (L1) / shards (L5) / gem (L15); каждый ПРИМИТИВ bonus-pill.
 *      Открыт → pill.setOpen(pct); ещё не открыт → pill.setLocked(unlockLevel).
 *   #3 Имя тоньше и меньшим кеглем (font-weight:500, font-size:--gnc-name-size).
 *   #4 Цена над «Улучшить» — структурно уже есть (cost > btn в .gnome-card-end space-between).
 *
 * SCOPE: построить карточку-плашку гнома через trophy-card (не копируя его логику). Принимает
 * `GnomeState`; данные маппятся в trophy-секции. Drill-down (тап по герою) → onOpenDetail.
 * NOT IN SCOPE: кольцо камней (owner: «без камней»), карат-бейдж (нет механики),
 * серверные мутации (GN-4), балансовые проценты (GN-5 TBD economy-balancer).
 *
 * Cross-references:
 *   - components/trophy-card.ts (каркас-ствол: row 3 секций + герой-зона + статус).
 *   - components/jewelry-card.ts (сестра-ветка: зверь+кольцо вместо гнома).
 *   - components/meta-badge.ts (уровень-примитив).
 *   - components/bonus-pill.ts (бонус-чип «иконка + +X%»; 3 штуки в столбце).
 *   - lib/gnome-state.ts (модель + gnomeBonusPercent + unlockLevelFor — single source).
 *   - components/meta-icons.ts (gnomeGlyph / ICON_GOLD / ICON_SHARD / gemGlyph).
 *   - gnome-card.css (co-located; размеры — токены; без верхних капов).
 */

import './gnome-card.css';

import type { ColorIndex } from '@lines98/shared/types/game';

import { formatRuNumber } from '../lib/format.js';
import { t } from '../lib/i18n/index.js';
import {
  gnomeName,
  formatGnomeBonusPercent,
  gnomeBonusPercent,
  isMaxLevel,
  isUnlocked,
  unlockLevelFor,
  type GnomeBonusKind,
  type GnomeState,
} from '../lib/gnome-state.js';
import { createBonusPill, type BonusPillHandle } from './bonus-pill.js';
import {
  createPrestigeBadge,
  type PrestigeBadgeHandle,
  type PrestigeTier,
} from './meta-badge.js';
import { ICON_GOLD, ICON_SHARD, gemGlyph, gnomeFullGlyph } from './meta-icons.js';
import { createTrophyCard } from './trophy-card.js';

// =============================================================================
// Model + API
// =============================================================================

/** Цена прокачки гнома (config GN-7 `[TBD balance]`; экран передаёт когда появится). */
export interface GnomeCardCost {
  readonly gold: number;
  readonly shards: number;
}

export interface GnomeCardOptions {
  /** Состояние гнома (color / level / prestige_count). */
  readonly state: GnomeState;
  /**
   * Цена прокачки (золото+осколки). Опц.: до balance (GN-7) не передаётся → цена не
   * рисуется (место зарезервировано). По карману — управляет акцентом кнопки.
   */
  readonly cost?: GnomeCardCost;
  /** Хватает ли ресурсов на прокачку. По умолчанию true. */
  readonly affordable?: boolean;
  /**
   * Действие кнопки (прокачать ИЛИ «Максимум» на L20). UI-фаза (GN-1) — заглушка-affordance.
   * Получает цвет гнома.
   */
  readonly onAction?: (color: ColorIndex) => void;
  /** Открыть деталь гнома (тап по герою) — drill-down (как у зверей). */
  readonly onOpenDetail?: (color: ColorIndex) => void;
}

export interface GnomeCardHandle {
  readonly element: HTMLElement;
  readonly color: ColorIndex;
  /** Обновить состояние (+ опц. цену/affordability) после ресинка server-truth (GN-4). */
  update(state: GnomeState, opts?: { cost?: GnomeCardCost; affordable?: boolean }): void;
  /** Pending-блокировка кнопки на время полёта мутации. */
  setPending(pending: boolean): void;
  /** Текущее состояние гнома. */
  state(): GnomeState;
  destroy(): void;
}

// =============================================================================
// Конфиг 3 бонус-строк (owner-directive 2026-06-14 правка #2 — столбец gold/shards/gem).
// =============================================================================

/** Мета-описание каждого из 3 бонусов для рендера pill-столбца. */
interface BonusRowDef {
  readonly kind: GnomeBonusKind;
  /** i18n-ключ подписи бонуса; текст резолвится `t()` per-mount (экран ремонтируется на смену языка). */
  readonly labelKey: string;
  readonly icon: (color: ColorIndex) => string;
}

/** Три бонуса в порядке отображения: gold → shards → gem. */
const BONUS_ROWS: readonly BonusRowDef[] = [
  { kind: 'gold', labelKey: 'game.gnomeCard.bonusGold', icon: () => ICON_GOLD },
  { kind: 'shards', labelKey: 'game.gnomeCard.bonusShards', icon: () => ICON_SHARD },
  { kind: 'gem', labelKey: 'game.gnomeCard.bonusGem', icon: (c) => gemGlyph(c) },
];

/** Построить карточку гнома (горизонтальная плашка = ветка trophy-card, как jewelry-card). */
export function createGnomeCard(opts: GnomeCardOptions): GnomeCardHandle {
  const color = opts.state.color;
  const { onAction, onOpenDetail } = opts;
  let current: GnomeState = opts.state;
  let currentCost: GnomeCardCost | undefined = opts.cost;
  let currentAffordable: boolean = opts.affordable ?? true;
  const name = gnomeName(color);

  // Общий каркас-трофей (ствол): row[hero · mid · end] + статус + герой-зона drill-down.
  // Ветка «Гномы»: наполняет слоты спрайтом гнома + имя/уровень/бонусы/кнопка.
  // data-color (не data-type): тесты и стили обращаются по цвету гнома.
  const frame = createTrophyCard({
    variant: 'gnome-card',
    dataKey: 'color',
    dataValue: String(color),
    ...(onOpenDetail ? { onOpenDetail: () => onOpenDetail(color) } : {}),
  });
  const root = frame.root;

  // --- СЕКЦИЯ 1: герой — спрайт гнома во всю высоту (правка #1).
  //     CSS: align-self:stretch + aspect-ratio:1/1 + padding-зазор (gnome-card.css).
  //     Героя помечаем gnome-card-hero для surface-стилей. ---
  const hero = frame.hero;
  hero.classList.add('gnome-card-hero');
  if (onOpenDetail) {
    hero.classList.add('gnome-card-zone');
    frame.setHeroLabel(t('game.gnomeCard.unlockAria', { name }));
  }
  const spriteWrap = document.createElement('span');
  spriteWrap.className = 'gnome-card-sprite';
  spriteWrap.setAttribute('aria-hidden', 'true');
  spriteWrap.innerHTML = gnomeFullGlyph(color); /* full/ — тесно обрезанный гном (не квадратный sprite с полями) → прижим влево работает */
  hero.appendChild(spriteWrap);

  // Резерв ширины героя под ПОЛНЫЙ (hover) размер гнома через его аспект (no-hardcode —
  // из реальных пропорций PNG, разных у каждого цвета). Без этого ширина героя следует за
  // растущей по высоте картинкой → инфо-блок с бонусами «едет» вправо на ховере. CSS читает
  // --gnc-aspect (aspect-ratio героя): высота фикс → ширина фикс → гном растёт внутри, бонусы стоят.
  const spriteImg = spriteWrap.querySelector('img');
  if (spriteImg) {
    const applyAspect = () => {
      if (spriteImg.naturalWidth > 0 && spriteImg.naturalHeight > 0) {
        hero.style.setProperty('--gnc-aspect', `${spriteImg.naturalWidth} / ${spriteImg.naturalHeight}`);
      }
    };
    if (spriteImg.complete) applyAspect();
    else spriteImg.addEventListener('load', applyAspect, { once: true });
  }

  // Престиж-бейдж в ЛЕВОМ-ВЕРХНЕМ углу рамки гнома (owner-directive 2026-06-14, Вариант 1).
  // Показываем ТОЛЬКО при prestigeCount ≥ 1 (progressive disclosure — у большинства 0, место
  // не резервируем). Привязан к идентичности гнома (угол портрета), не занимает layout-строку.
  const prestigeBadge: PrestigeBadgeHandle = createPrestigeBadge({
    className: 'gnome-card-prestige',
    numClassName: 'gnome-card-badge-num',
    ariaPrefix: t('game.gnomeCard.prestigeCount'),
  });
  prestigeBadge.element.hidden = true;
  hero.appendChild(prestigeBadge.element);

  // --- СЕКЦИЯ 2: инфо — имя (тоньше, правка #3) + уровень-бейдж + 3 бонуса столбцом (правка #2). ---
  const mid = frame.mid;
  mid.classList.add('gnome-card-mid');

  const topline = document.createElement('div');
  topline.className = 'gnome-card-topline';

  // Имя гнома: font-weight:500, font-size:--gnc-name-size (CSS правка #3).
  const nameEl = document.createElement('div');
  nameEl.className = 'gnome-card-name';
  nameEl.textContent = name;

  // Уровень — компактный ТЕКСТ «ур. N» (без круга), в одну строку с именем (owner-directive
  // 2026-06-14: «имя+уровень в одну строку, круг не нужен»). Класс .gnome-card-level сохранён
  // (на него смотрят viewport-тесты). На максимуме — accent-стиль (gnome-card-lvl--max).
  const lvlEl = document.createElement('span');
  lvlEl.className = 'gnome-card-lvl gnome-card-level';
  topline.append(nameEl, lvlEl);

  // Блок 3 бонусов СТОЛБЦОМ (правка #2). Каждый — ПРИМИТИВ bonus-pill.
  // Высота зарезервирована CSS (--jc-bonuses-h = 3 ряда).
  // pills[k] соответствует BONUS_ROWS[k] — постоянные DOM-узлы, обновляются через handle.
  const bonuses = document.createElement('div');
  bonuses.className = 'gnome-card-bonuses';
  const pills: BonusPillHandle[] = BONUS_ROWS.map((def) =>
    createBonusPill({
      icon: def.icon(color),
      label: t(def.labelKey),
      className: 'gnome-card-bonus',
      iconClassName: 'gnome-card-bonus-ic',
      pctClassName: 'gnome-card-bonus-pct',
    }),
  );
  pills.forEach((p) => bonuses.appendChild(p.element));
  mid.append(topline, bonuses);

  // Правый столбец каркаса (trophy-card-end) НЕ используем — действие переносим вниз во всю
  // ширину карточки (owner-directive 2026-06-14). CSS: .gnome-card-end { display:none }.
  frame.end.classList.add('gnome-card-end');

  // --- СЕКЦИЯ 3: действие — ЕДИНАЯ кнопка во всю ширину карточки (owner-directive 2026-06-14).
  //     Слитный контрол: цена ресурсов слева + знак «▲» справа (улучшить). При максимуме →
  //     текст «Максимум» (disabled). Кнопка ПОД рядом [гном · бонусы], шириной = вся карточка. ---
  const action = document.createElement('button');
  action.type = 'button';
  action.className = 'btn gnome-card-action';
  action.addEventListener('click', (e) => {
    e.stopPropagation();
    onAction?.(color);
  });
  const cost = document.createElement('span');
  cost.className = 'gnome-card-cost';
  const actionLabel = document.createElement('span');
  actionLabel.className = 'gnome-card-action-label';
  const arrow = document.createElement('span');
  arrow.className = 'gnome-card-arrow';
  arrow.setAttribute('aria-hidden', 'true');
  arrow.textContent = '▲'; // знак «улучшить» (level-up)
  action.append(cost, actionLabel, arrow);

  // --- LOCKED-подпись «Откроется за уровень» (GN-2b, AC-3) — занимает место кнопки в
  //     locked-слоте (силуэт + подпись, БЕЗ кнопок прокачки, AC-4). Текст fluid (CSS §8.10,
  //     не статичный px). Создаётся всегда, в DOM только когда locked (toggle ниже). ---
  const lockedLabel = document.createElement('div');
  lockedLabel.className = 'gnome-card-locked-label';
  lockedLabel.textContent = t('game.gnomeCard.locked');

  // Статус-строка — из каркаса; помечаем gnome-card-классом.
  const statusEl = root.querySelector<HTMLElement>('.trophy-card-status')!;
  statusEl.classList.add('gnome-card-status');
  // Кнопка-действие — ПОД рядом секций, НАД статус-строкой (полная ширина карточки).
  // Открыт → action в DOM; locked → action ИЗ DOM (AC-4 `.gnome-card-action` count 0),
  // вместо неё lockedLabel. Переключение — `setActionRow` (вызывается из applyState).
  root.insertBefore(action, statusEl);

  /**
   * Переключить «действие-ряд» между открытой кнопкой и locked-подписью. Locked:
   * кнопка УДАЛЯЕТСЯ из DOM (AC-4 — `.gnome-card-action`/`.gnome-card-btn` count 0), на её
   * место — `.gnome-card-locked-label`. Открыт: наоборот. attach-не-перерисовка (§3).
   */
  function setActionRow(unlocked: boolean): void {
    if (unlocked) {
      if (lockedLabel.parentNode) lockedLabel.parentNode.removeChild(lockedLabel);
      if (!action.parentNode) root.insertBefore(action, statusEl);
      // Открытый: карточка НЕ button-роль (интерактив — герой/кнопка внутри, §8.7).
      root.removeAttribute('role');
      root.removeAttribute('tabindex');
      root.removeAttribute('aria-label');
    } else {
      if (action.parentNode) action.parentNode.removeChild(action);
      if (!lockedLabel.parentNode) root.insertBefore(lockedLabel, statusEl);
      // Locked: вся карточка — тап-таргет открытия teaser-детали (a11y, AC-7).
      if (onOpenDetail) {
        root.setAttribute('role', 'button');
        root.setAttribute('tabindex', '0');
        root.setAttribute('aria-label', t('game.gnomeCard.lockedTeaserAria'));
      }
    }
  }

  // LOCKED-слот: тап/Enter ПО ВСЕЙ карточке открывает teaser-деталь (AC-7 — у locked нет
  // action-кнопки, §8.7 разрешает whole-card click без icon-button-конфликта). Открытый
  // слот так НЕ делает (там клик по герою = деталь, клик по кнопке = действие — §8.7);
  // role/tabindex ставятся ТОЛЬКО в locked-ветке (setActionRow). Дубль-открытия нет: open()
  // экрана идемпотентен. Тактильность locked-слота — наследует карточку (≥44px).
  if (onOpenDetail) {
    root.addEventListener('click', () => {
      if (!isUnlocked(current)) onOpenDetail(color);
    });
    root.addEventListener('keydown', (e: KeyboardEvent) => {
      if (!isUnlocked(current) && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onOpenDetail(color);
      }
    });
  }

  applyState(opts.state, opts.cost, opts.affordable ?? true);

  /**
   * Обновить 3 бонус-пилюли по уровню гнома (правка #2).
   * Каждый BONUS_ROWS[k] → pills[k]:
   *   · Открыт (level ≥ unlockLevel) → pill.setOpen(«+X.X%») — растущее ДРОБНОЕ значение,
   *     совпадающее с серверной добычей (gnomeBonusPercent = shared gnomeContribution × 100).
   *   · Закрыт → pill.setLocked(unlockLevel) — показывает порог «L5»/«L15».
   * Формат «+X.X%» — единый хелпер `formatGnomeBonusPercent` (тот же зовёт деталь, AC-8).
   * DOM-узлы не пересоздаются (постоянные), меняется только textContent (AC-9 instant-hydration).
   */
  function applyBonuses(state: GnomeState): void {
    for (let k = 0; k < BONUS_ROWS.length; k++) {
      const def = BONUS_ROWS[k]!;
      const pill = pills[k]!;
      const pct = gnomeBonusPercent(def.kind, state);
      if (pct !== null) {
        // Открыт — растущее «+X.X%» (дробное, не округлённое; камень <1% тоже виден).
        const display = formatGnomeBonusPercent(pct);
        // aria = озвучиваемая форма «+X.X%» (символы +/% → слова на активном языке;
        // value = числовое тело без +/%). i18n: game.gnomeCard.bonusPctAria.
        const value = display.replace(/[+%]/g, '').trim();
        pill.setOpen(display, t('game.gnomeCard.bonusPctAria', { value }));
      } else {
        // Закрыт — показываем порог «L?» (unlockLevelFor из lib/gnome-state, single source).
        pill.setLocked(unlockLevelFor(def.kind));
      }
    }
  }

  /** Применить состояние гнома к DOM (уровень / 3 бонуса / цена / кнопка). */
  function applyState(state: GnomeState, costVal?: GnomeCardCost, affordable = true): void {
    current = state;
    currentCost = costVal;
    currentAffordable = affordable;
    const { level } = state;

    // --- LOCKED-ветка (GN-2b, rev 0.3 §2.0 / AC-3/AC-4): силуэт + подпись «Откроется за
    //     уровень», БЕЗ уровня/бонусов/кнопки. Силуэт = ТОТ ЖЕ спрайт-источник
    //     (gnomeFullGlyph, уже в hero) — locked-вид даёт CSS (.gnome-card--locked filter,
    //     AC-5). Раскладка/размер слота НЕ меняются (тот же hero-бокс → reveal не «прыгает»,
    //     AC-20). ---
    if (!isUnlocked(state)) {
      root.classList.add('gnome-card--locked');
      root.classList.remove('gnome-card--max', 'gnome-card--unaffordable');
      // СЕКРЕТ locked-слота (§2.0/§5.1, AC-3): НИ имя, НИ цвет НЕ должны быть читаемы — иначе
      // сюрприз раскрыт. Имя гнома = его ЦВЕТ (RU «Красный»/«Зелёный»/…), а 3-й бонус-чип
      // показывает самоцвет ЕГО цвета (рубин=красный…) → каждый выдаёт идентичность. Поэтому:
      //   · ИМЯ → нейтральная «???» (RU-цвет-имя НЕ показываем; имя O-5 ещё не выбрано —
      //     locked это и маскирует). Уровень — пусто.
      //   · БЛОК БОНУСОВ скрыт CSS-правилом `.gnome-card--locked .gnome-card-bonuses`
      //     (display:none) — ни цвет-специфичной gem-иконки, ни значений.
      // Видим только: серый силуэт спрайта (CSS-filter, AC-5) + «???» + locked-подпись + веха.
      nameEl.textContent = '???';
      lvlEl.textContent = '';
      lvlEl.classList.remove('gnome-card-lvl--max');
      // Престиж-бейдж locked-слот не показывает (бонусы скрывает CSS по locked-классу).
      prestigeBadge.element.hidden = true;
      // Действие-ряд → locked-подпись (кнопка из DOM, AC-4).
      setActionRow(false);
      return;
    }
    // Открытый слот: снять locked-класс, ВЕРНУТЬ настоящее имя, вернуть кнопку в ряд.
    root.classList.remove('gnome-card--locked');
    nameEl.textContent = name;
    setActionRow(true);

    const max = isMaxLevel(level);

    // Уровень — примитив meta-badge.
    lvlEl.textContent = t('game.gnomeCard.level', { n: level });
    lvlEl.classList.toggle('gnome-card-lvl--max', max);

    // Престиж-бейдж (угол гнома) — ТОЛЬКО при ≥1 (иначе скрыт, место не занимает).
    // Престиж: примитив сам скрывается при 0 (свойство примитива). Тир материала растёт с числом
    // престижей (1=бронза … 4+=бриллиант) — иначе тир 0 = «тусклый камень», значок блёклый.
    const tier = Math.max(0, Math.min(4, state.prestigeCount)) as PrestigeTier;
    prestigeBadge.set(state.prestigeCount, tier);

    // 3 бонуса столбцом (правка #2).
    applyBonuses(state);

    // Кнопка во всю ширину: на L20 → «Престиж» (enabled — действие престижа, §2.4; когерентно
    // с деталью §8.8 gnome-detail: max → слово «Престиж», без цены/без `▲`); иначе цена + «▲».
    if (max) {
      cost.hidden = true;
      cost.innerHTML = '';
      actionLabel.textContent = t('game.gnomeCard.actionPrestige');
      arrow.hidden = true;
      // L20 — это действие престижа (не «потолок-заглушка»): кнопка АКТИВНА (экран открывает
      // confirm → prestigeGnome). Не disabled (иначе тап невозможен, AC-10).
      action.disabled = false;
      action.setAttribute('aria-label', t('game.gnomeCard.prestigeAria', { name }));
      root.classList.add('gnome-card--max');
      root.classList.add('gnome-card--prestige');
      root.classList.remove('gnome-card--unaffordable');
      // На L20 кнопка не «primary»-апгрейд (когерентно с деталью: primary только в апгрейде).
      action.classList.remove('primary');
      return;
    }
    root.classList.remove('gnome-card--prestige');
    root.classList.remove('gnome-card--max');
    arrow.hidden = false;
    action.disabled = !affordable;
    action.setAttribute('aria-label', t('game.gnomeCard.upgradeAria', { name, level: level + 1 }));

    // Цена ВНУТРИ кнопки: рисуем когда экран передал число (GN-7 TBD balance); иначе «Улучшить».
    if (costVal) {
      cost.hidden = false;
      actionLabel.textContent = '';
      // Рисуем ТОЛЬКО ненулевые валюты (owner-directive 2026-06-22): если осколки не нужны
      // (L1..14: shards=0) — показываем просто «🪙 8», без «💎 0». На L15+ — обе валюты.
      const pairs: string[] = [];
      const ariaParts: string[] = [];
      if (costVal.gold > 0) {
        pairs.push(
          `<span class="gnome-card-cost-pair"><span class="gnome-card-cost-ic" aria-hidden="true">${ICON_GOLD}</span>${formatRuNumber(
            costVal.gold,
          )}</span>`,
        );
        ariaParts.push(t('game.gnomeCard.priceGold', { n: formatRuNumber(costVal.gold) }));
      }
      if (costVal.shards > 0) {
        pairs.push(
          `<span class="gnome-card-cost-pair"><span class="gnome-card-cost-ic" aria-hidden="true">${ICON_SHARD}</span>${formatRuNumber(
            costVal.shards,
          )}</span>`,
        );
        ariaParts.push(t('game.gnomeCard.priceShards', { n: formatRuNumber(costVal.shards) }));
      }
      cost.innerHTML = pairs.join('');
      cost.setAttribute('aria-label', t('game.gnomeCard.priceAria', { parts: ariaParts.join(', ') }));
    } else {
      cost.hidden = true;
      cost.innerHTML = '';
      cost.removeAttribute('aria-label');
      actionLabel.textContent = t('game.gnomeCard.actionUpgrade');
    }
    root.classList.toggle('gnome-card--unaffordable', !affordable);
    action.classList.toggle('primary', !action.disabled);
  }

  /** Восстановить enabled-состояние кнопки из текущей модели (без перерисовки). */
  function syncButtonEnabled(): void {
    // L20 = «Престиж» (АКТИВНА — это действие, не потолок-заглушка; AC-10). primary только в
    // апгрейде (когерентно с деталью §8.8). Иначе — по affordability.
    if (isMaxLevel(current.level)) {
      action.disabled = false;
      action.classList.remove('primary');
      return;
    }
    const disabled = !currentAffordable;
    action.disabled = disabled;
    action.classList.toggle('primary', !disabled);
  }

  return {
    element: root,
    color,
    update(state: GnomeState, o?: { cost?: GnomeCardCost; affordable?: boolean }): void {
      action.classList.remove('gnome-card-btn--pending');
      applyState(
        state,
        o && 'cost' in o ? o.cost : currentCost,
        o?.affordable ?? currentAffordable,
      );
      // applyState мог быть no-op guard'ом (если добавить): гарантированно вернуть кнопку.
      syncButtonEnabled();
    },
    setPending(pending: boolean): void {
      action.disabled = pending || action.disabled;
      action.classList.toggle('gnome-card-btn--pending', pending);
      action.setAttribute('aria-busy', pending ? 'true' : 'false');
    },
    state(): GnomeState {
      return current;
    },
    destroy(): void {
      if (root.parentNode) root.parentNode.removeChild(root);
    },
  };
}
